import React, { useEffect, useMemo } from "react";
import {
  Star,
  Quote,
  Building,
  Sparkles,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingTestimonials from "../components/MarketingTestimonials";
import { marketingNavLinks } from "../constants/marketingNav";

const stats = [
  { label: "Active Users", value: "50,000+" },
  { label: "Projects Completed", value: "1.2M+" },
  { label: "Average Rating", value: "4.9/5" },
  { label: "Time Saved/Week", value: "12hrs" }
];

const TestimonialsPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Testimonials") return { ...link, route: "/testimonials" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader navLinks={navLinks} ctaTarget="contact" />

      <main>
        <section className="curved-section shell-sky section-animate pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="section-eyebrow reveal">
              <CheckCircle className="w-4 h-4 inline mr-2" />
              Trusted by 500+ Teams
            </span>
            <h1 className="reveal stagger-1 mt-6 mb-8 text-navy">
              Loved by teams <br />
              <span className="text-blue-600">worldwide</span>
            </h1>
            <p className="text-lead reveal stagger-2 max-w-2xl mx-auto">
              Don't just take our word for it. Hear from the teams that use PMS 
              every day to build incredible products.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mt-16 reveal stagger-3">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl font-black text-navy mb-1">{stat.value}</div>
                  <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingTestimonials />

        <section className="shell-navy text-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="absolute top-0 right-0 -mr-20 -mt-20 opacity-10">
              <Quote className="w-96 h-96" />
            </div>
            
            <div className="grid lg:grid-cols-2 gap-20 items-center">
              <div className="reveal-left">
                <span className="section-eyebrow">Featured Story</span>
                <h2 className="mt-6 mb-8 text-white">
                  How TechCorp increased velocity by 40% with TeamBoard.
                </h2>
                <p className="text-blue-100 text-lg mb-10 leading-relaxed">
                  "Before TeamBoard, our projects were scattered across four different tools. 
                  Now, everything from requirements to secure deployment keys is in one place. 
                  The AI extraction alone has changed how we handle client briefs."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center">
                    <Building className="w-8 h-8 text-sky" />
                  </div>
                  <div>
                    <div className="text-xl font-bold">Maria Kim</div>
                    <div className="text-sky font-medium">Director of Operations, TechCorp</div>
                  </div>
                </div>
              </div>
              
              <div className="relative reveal-right">
                <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-[40px] p-10 shadow-2xl">
                  <div className="space-y-8">
                    {[
                      { label: "75% Faster Delivery", value: "w-3/4", color: "bg-green-400" },
                      { label: "90% Team Adoption", value: "w-[90%]", color: "bg-blue-400" },
                      { label: "50% Less Email", value: "w-1/2", color: "bg-purple-400" }
                    ].map((item, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex justify-between text-sm font-bold">
                          <span>{item.label}</span>
                        </div>
                        <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full ${item.value}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-12 pt-10 border-t border-white/10 text-center">
                    <button className="btn-primary !bg-white !text-navy hover:!bg-sky">
                      Read Case Study
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="shell-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="mb-16">Trusted by Industry Leaders</h2>
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20 opacity-40 grayscale reveal">
              {["TechCorp", "StartupX", "DesignLab", "CloudBase", "AgencyPro"].map((logo) => (
                <div key={logo} className="flex items-center gap-2 text-2xl font-black text-gray-800">
                  <Sparkles className="w-8 h-8" />
                  {logo}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="cta" className="shell-sky text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-navy">Ready to join them?</h2>
            <p className="text-lead mb-12">
              Start your 14-day free trial today. No credit card required. 
              Join 500+ teams that are already shipping faster with TeamBoard.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="btn-primary">
                Get Started Free
              </button>
              <button className="btn-secondary">
                Request a Demo
              </button>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default TestimonialsPage;
